import React, { useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ClearIcon from '@mui/icons-material/Clear';
import { FormattedMessage } from 'react-intl';
import FileCopySharpIcon from '@mui/icons-material/FileCopySharp';
import UndoSharpIcon from '@mui/icons-material/UndoSharp';
import { get } from 'lodash';
import classNames from 'classnames';
import {
  useFormErrorDialogContext,
  COMMENT_MAX_CHAR_LIMIT
} from 'modules/notes/common';
import { CharacterCounter } from 'modules/common';
import { useTextAreaScrollStyles } from 'modules/notes/hooks';
import { LabelValueAcceptedBy, TimeSignalAttribute } from 'graphql/types';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { useFetchSuggestions } from 'modules/notes/addNote/hooks';
import { useMessage } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { TimesheetLabel } from '../labelSchema';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2)
  },
  comment: {
    width: 'calc(100% - 16px)',
    margin: theme.spacing(0, 0, 1, 1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5)
  },
  input: {
    color: theme.palette.text.primary,
    '&:focus': {
      color: theme.palette.text.primary
    },
    '&::placeholder': {
      color: theme.palette.grey[900]
    }
  },
  materialUIInput: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingRight: theme.spacing(0.5)
  },
  inputAdornment: {
    height: '100%',
    margin: theme.spacing(0, 0.5)
  },
  button: {
    padding: theme.spacing(0),
    color: theme.palette.text.primary
  },
  copyCountContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  copyBtn: {
    margin: theme.spacing(1, 0, 0, 1),
    fontWeight: 600
  }
}));

interface CommentLabelComponentProps {
  label: TimesheetLabel;
  fieldName: string;
  placeHolder?: string;
  onChange: ControllerRenderProps['onChange'];
  attributes?: TimeSignalAttribute[] | undefined;
}

export enum COPY_BUTTON_TYPE {
  COPY = 'copy',
  UNDO = 'undo'
}

const CommentInputLabel = <FormattedMessage id="note.timesheetComment" />;

export const CommentInput: React.FC<CommentLabelComponentProps> = ({
  label,
  fieldName,
  onChange,
  placeHolder,
  attributes
}: CommentLabelComponentProps) => {
  const fetchSuggestions = useFetchSuggestions();
  const classes = useStyles();
  const classesScroll = useTextAreaScrollStyles();
  const { formState, getValues } = useFormContext();
  const {
    errorContext,
    setErrorContext,
    variant
  } = useFormErrorDialogContext();
  const { note: noteField } = useLabelsFormFieldContext();
  const [previousComment, setPreviousComment] = useState<string | null>(null);
  const [copyButtonType, setCopyButtonType] = useState<COPY_BUTTON_TYPE>(
    COPY_BUTTON_TYPE.COPY
  );
  const isNoteDescriptionDirty = get(formState.dirtyFields, noteField);
  const error = get(formState.errors, fieldName);
  const comment = useMemo(() => label?.value?.stringValue ?? '', [label]);
  const setCommentValue = useCallback(
    (value: string | null) => {
      onChange({
        ...label,
        value: value
          ? {
              idValue: null,
              numericValue: null,
              stringValue: value
            }
          : null,
        valueSource: 'USER',
        acceptedBy: LabelValueAcceptedBy.User
      });
    },
    [label, onChange]
  );
  const clearComment = useCallback(() => {
    setCommentValue(null);
    setPreviousComment(null);
    setErrorContext((prev) => {
      return { ...prev, ...{ commentExceeded: false } };
    });
    setCopyButtonType(COPY_BUTTON_TYPE.COPY);
  }, [setCommentValue, setErrorContext]);

  const updateComment = useCallback(
    (event) => {
      const description = event.target.value;
      const isError = Boolean(
        (description?.length ?? 0) > COMMENT_MAX_CHAR_LIMIT
      );
      setErrorContext((prev) => {
        if (prev.commentExceeded !== isError) {
          return { ...prev, commentExceeded: isError };
        }
        return prev;
      });
      setPreviousComment(description);
      setCommentValue(description);
      if (variant === 'note') fetchSuggestions({ attributes });
      if ((description || '').length === 0) {
        setCopyButtonType(COPY_BUTTON_TYPE.COPY);
      }
    },
    [
      attributes,
      setCommentValue,
      setPreviousComment,
      setErrorContext,
      fetchSuggestions,
      variant
    ]
  );

  const btnIcon = useMemo(
    () =>
      copyButtonType === COPY_BUTTON_TYPE.COPY ? (
        <FileCopySharpIcon />
      ) : (
        <UndoSharpIcon />
      ),
    [copyButtonType]
  );

  const btnText = useMemo(
    () =>
      copyButtonType === COPY_BUTTON_TYPE.COPY ? (
        <FormattedMessage id="note.copyComment" />
      ) : (
        <FormattedMessage id="note.undoComment" />
      ),
    [copyButtonType]
  );

  const inputProps = useMemo(
    () => ({
      'data-testid': 'controlled-comment-input',
      className: classes.input
    }),
    [classes.input]
  );

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const message = useMessage('note.clearComment');

  const InputProps = useMemo(
    () => ({
      endAdornment: comment ? (
        <InputAdornment className={classes.inputAdornment} position="end">
          <IconButton
            className={classes.button}
            data-testid={'comment-clear-button'}
            onClick={clearComment}
            size="large"
            aria-label={isTiwScreenReaderEnabled ? message : undefined}
          >
            <ClearIcon fontSize={'small'} />
          </IconButton>
        </InputAdornment>
      ) : (
        <></>
      ),
      className: classes.materialUIInput
    }),
    [
      classes.button,
      classes.inputAdornment,
      classes.materialUIInput,
      clearComment,
      comment,
      isTiwScreenReaderEnabled,
      message
    ]
  );

  const onCopyOrUndo = useCallback(() => {
    setCopyButtonType((prev) => {
      switch (prev) {
        case COPY_BUTTON_TYPE.COPY: {
          const values = getValues();
          const noteDescription = get(values, noteField);
          setCommentValue(noteDescription);
          return COPY_BUTTON_TYPE.UNDO;
        }
        case COPY_BUTTON_TYPE.UNDO:
          {
            const value =
              previousComment && Boolean(previousComment.length)
                ? previousComment
                : null;
            setCommentValue(value);
          }
          return COPY_BUTTON_TYPE.COPY;
      }
    });
  }, [getValues, noteField, setCommentValue, previousComment]);
  return (
    <div className={classes.container} data-testid="comment-input">
      <TextField
        data-testid="controlled-comment-text-field"
        value={comment}
        onChange={updateComment}
        className={classNames(classes.comment, classesScroll.scrollBar)}
        variant="outlined"
        multiline
        error={Boolean(error) || errorContext.commentExceeded}
        maxRows={2}
        placeholder={placeHolder}
        inputProps={inputProps}
        InputProps={InputProps}
        size="small"
        label={CommentInputLabel}
      />
      <div className={classes.copyCountContainer}>
        <Button
          data-testid="comment-copy-undo-btn"
          variant="outlined"
          color="primary"
          onClick={onCopyOrUndo}
          className={classes.copyBtn}
          startIcon={btnIcon}
          disabled={!isNoteDescriptionDirty}
        >
          {btnText}
        </Button>

        <CharacterCounter
          text={comment}
          totalChar={COMMENT_MAX_CHAR_LIMIT}
          hasError={Boolean(error || errorContext.commentExceeded)}
        />
      </div>
    </div>
  );
};
